import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { DialogConfirm, FieldWrapper, PageHeader, StepActions } from "@components/ui_v2";
import { useRequestPasswordChangeMutation } from "@api/onboarding";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { EMAIL } from "@utils/expreciones";
import { PATHS_AUTH_V2 } from "@helpers/constantes";

/**
 * Recuperación de contraseña con el diseño v2: mismo servicio que `CambiarContrasena` (legacy),
 * que envía al correo el enlace para crear la contraseña nueva.
 */
export const RecuperarContrasenaV2 = () => {
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();
  const [requestPasswordChange] = useRequestPasswordChangeMutation();
  const [result, setResult] = useState<"" | "success" | "error">("");

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: yup.object({
      email: yup
        .string()
        .trim("El correo no puede tener espacios al inicio o al final")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)")
        .required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        await requestPasswordChange({ username: values.email, company: interfaceConfig.name.toUpperCase() }).unwrap();
        setResult("success");
      } catch (error) {
        // Mismo mensaje que el legacy: el servicio responde error cuando no existe la cuenta.
        Sentry.captureException(error, { extra: { component: "RecuperarContrasenaV2-request_change" } });
        setResult("error");
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  return (
    <Box>
      <PageHeader
        eyebrow="Recuperar contraseña"
        title="¿Olvidaste tu contraseña?"
        subtitle="Ingresa tu correo electrónico y te enviaremos un enlace para que generes una nueva contraseña."
      />

      <Paper variant="lendia_paper">
        <Box component="form" noValidate onSubmit={formik.handleSubmit}>
          <FieldWrapper label="Correo electrónico" error={(formik.touched.email && formik.errors.email) || ""}>
            <TextField
              size="lendia_input"
              fullWidth
              name="email"
              autoComplete="username"
              placeholder="tucorreo@empresa.com"
              value={formik.values.email}
              onChange={(e) => formik.setFieldValue("email", e.target.value.trim())}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting}
              inputProps={{ "data-testid": "recuperar-contrasena-email-input" }}
            />
          </FieldWrapper>
        </Box>
      </Paper>

      <StepActions
        onBack={() => navigate(PATHS_AUTH_V2.login.path)}
        backLabel="Volver a iniciar sesión"
        onNext={formik.submitForm}
        nextLabel={formik.isSubmitting ? "Enviando…" : "Enviar enlace"}
        nextDisabled={!formik.isValid || formik.isSubmitting}
        backTestId="recuperar-contrasena-back-button"
        nextTestId="recuperar-contrasena-submit-button"
      />

      <DialogConfirm
        open={result === "success"}
        tone="success"
        icon={<CheckCircleOutline />}
        title="¡Correo enviado!"
        message="Hemos enviado un enlace a tu correo electrónico. Sigue las instrucciones para restablecer tu contraseña."
        confirmLabel="Cerrar"
        onConfirm={() => setResult("")}
        onClose={() => setResult("")}
        confirmTestId="recuperar-contrasena-success-close-button"
      />
      <DialogConfirm
        open={result === "error"}
        tone="danger"
        icon={<ErrorOutline />}
        title="Cuenta no encontrada"
        message="No encontramos una cuenta asociada a este correo electrónico. Por favor verifica el correo ingresado e inténtalo de nuevo."
        confirmLabel="Cerrar"
        onConfirm={() => setResult("")}
        onClose={() => setResult("")}
        confirmTestId="recuperar-contrasena-error-close-button"
      />
    </Box>
  );
};
